/** Route table. Page components are lazy-loaded per STANDARDSv2 §Code Organization. */

import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { FullPageSpinner } from '@/components/common/FullPageSpinner';
import { AdminRoute, GuestRoute, PermissionRoute, ProtectedRoute } from '@/components/common/RouteGuards';
import { AppLayout } from '@/components/layout/AppLayout';

const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const SsoCallbackPage = lazy(() =>
  import('@/pages/SsoCallbackPage').then((m) => ({ default: m.SsoCallbackPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const TicketBoardPage = lazy(() =>
  import('@/pages/tickets/TicketBoardPage').then((m) => ({ default: m.TicketBoardPage })),
);
const TicketDetailPage = lazy(() =>
  import('@/pages/tickets/TicketDetailPage').then((m) => ({ default: m.TicketDetailPage })),
);
const CompaniesPage = lazy(() =>
  import('@/pages/crm/CompaniesPage').then((m) => ({ default: m.CompaniesPage })),
);
const CompanyDetailPage = lazy(() =>
  import('@/pages/crm/CompanyDetailPage').then((m) => ({ default: m.CompanyDetailPage })),
);
const ContactsPage = lazy(() =>
  import('@/pages/crm/ContactsPage').then((m) => ({ default: m.ContactsPage })),
);
const DocsViewer = lazy(() => import('@/pages/DocsViewer').then((m) => ({ default: m.DocsViewer })));
const BedrockRegistrationPage = lazy(() =>
  import('@/pages/admin/BedrockRegistrationPage').then((m) => ({
    default: m.BedrockRegistrationPage,
  })),
);
const CustomFieldsPage = lazy(() =>
  import('@/pages/admin/CustomFieldsPage').then((m) => ({ default: m.CustomFieldsPage })),
);
const TimesheetPage = lazy(() =>
  import('@/pages/time/TimesheetPage').then((m) => ({ default: m.TimesheetPage })),
);
const TimeApprovalsPage = lazy(() =>
  import('@/pages/time/TimeApprovalsPage').then((m) => ({ default: m.TimeApprovalsPage })),
);
const WorkflowsPage = lazy(() =>
  import('@/pages/admin/WorkflowsPage').then((m) => ({ default: m.WorkflowsPage })),
);
const WorkflowEditorPage = lazy(() =>
  import('@/pages/admin/WorkflowEditorPage').then((m) => ({ default: m.WorkflowEditorPage })),
);
const DeviceSourcesPage = lazy(() =>
  import('@/pages/admin/DeviceSourcesPage').then((m) => ({ default: m.DeviceSourcesPage })),
);
const ExternalDataMaintenancePage = lazy(() =>
  import('@/pages/admin/ExternalDataMaintenancePage').then((m) => ({
    default: m.ExternalDataMaintenancePage,
  })),
);
const KnowledgeBasePage = lazy(() =>
  import('@/pages/kb/KnowledgeBasePage').then((m) => ({ default: m.KnowledgeBasePage })),
);
const AssetsPage = lazy(() =>
  import('@/pages/assets/AssetsPage').then((m) => ({ default: m.AssetsPage })),
);
const AssetDetailPage = lazy(() =>
  import('@/pages/assets/AssetDetailPage').then((m) => ({ default: m.AssetDetailPage })),
);
const ProjectsPage = lazy(() =>
  import('@/pages/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const ProjectDetailPage = lazy(() =>
  import('@/pages/projects/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })),
);
const ClassificationPage = lazy(() =>
  import('@/pages/admin/ClassificationPage').then((m) => ({ default: m.ClassificationPage })),
);
const ContractsPage = lazy(() =>
  import('@/pages/admin/ContractsPage').then((m) => ({ default: m.ContractsPage })),
);
const EscalationPathsPage = lazy(() =>
  import('@/pages/admin/EscalationPathsPage').then((m) => ({ default: m.EscalationPathsPage })),
);
const EscalationPathEditorPage = lazy(() =>
  import('@/pages/admin/EscalationPathEditorPage').then((m) => ({
    default: m.EscalationPathEditorPage,
  })),
);
const SlaPoliciesPage = lazy(() =>
  import('@/pages/admin/SlaPoliciesPage').then((m) => ({ default: m.SlaPoliciesPage })),
);
const SlaPolicyEditorPage = lazy(() =>
  import('@/pages/admin/SlaPolicyEditorPage').then((m) => ({ default: m.SlaPolicyEditorPage })),
);
const MirrorPage = lazy(() =>
  import('@/pages/admin/MirrorPage').then((m) => ({ default: m.MirrorPage })),
);
const CompanyMatchingPage = lazy(() =>
  import('@/pages/admin/CompanyMatchingPage').then((m) => ({ default: m.CompanyMatchingPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/admin/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

export function App() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        {/*
          Not an OAuth callback — Bedrock 302s here with the issued token. Deliberately outside
          GuestRoute: the page must be able to run completeSsoLogin and then navigate itself.
        */}
        <Route path="/sso-callback" element={<SsoCallbackPage />} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          {/*
            Each screen is gated by the permission its API calls require, so a user without it
            gets an explanatory screen rather than a board that 403s on every request.
          */}
          <Route
            path="/tickets"
            element={
              <PermissionRoute permission="tickets.view">
                <TicketBoardPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tickets/:ticketId"
            element={
              <PermissionRoute permission="tickets.view">
                <TicketDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <PermissionRoute permission="crm.companies.view">
                <CompaniesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/companies/:companyId"
            element={
              <PermissionRoute permission="crm.companies.view">
                <CompanyDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/contacts"
            element={
              <PermissionRoute permission="crm.contacts.view">
                <ContactsPage />
              </PermissionRoute>
            }
          />
          {/* Admin-guide access is additionally gated inside DocsViewer and in the sidebar. */}
          <Route path="/docs/:slug" element={<DocsViewer />} />
          <Route
            path="/admin/bedrock-registration"
            element={
              <PermissionRoute permission="admin.users.manage">
                <BedrockRegistrationPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/custom-fields"
            element={
              <PermissionRoute permission="admin.custom-fields.manage">
                <CustomFieldsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/external-data"
            element={
              <PermissionRoute permission="integrations.mirror.view">
                <MirrorPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/time/timesheet"
            element={
              <PermissionRoute permission="time.view">
                <TimesheetPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/time/approvals"
            element={
              <PermissionRoute permission="time.approve">
                <TimeApprovalsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/workflows"
            element={
              <PermissionRoute permission="sla.workflows.manage">
                <WorkflowsPage />
              </PermissionRoute>
            }
          />
          {/* §5.6: full-page editor at /admin/workflows/:id */}
          <Route
            path="/admin/workflows/:workflowId"
            element={
              <PermissionRoute permission="sla.workflows.manage">
                <WorkflowEditorPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/sla"
            element={
              <PermissionRoute permission="sla.manage">
                <SlaPoliciesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/sla/:policyId"
            element={
              <PermissionRoute permission="sla.manage">
                <SlaPolicyEditorPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/kb"
            element={
              <PermissionRoute permission="kb.view">
                <KnowledgeBasePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <PermissionRoute permission="assets.view">
                <AssetsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/assets/:assetId"
            element={
              <PermissionRoute permission="assets.view">
                <AssetDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <PermissionRoute permission="projects.view">
                <ProjectsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <PermissionRoute permission="projects.view">
                <ProjectDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/device-sources"
            element={
              <PermissionRoute permission="assets.manage">
                <DeviceSourcesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/external-data"
            element={
              <PermissionRoute permission="integrations.mirror.sync">
                <ExternalDataMaintenancePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/classification"
            element={
              <PermissionRoute permission="admin.classification.manage">
                <ClassificationPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/contracts"
            element={
              <PermissionRoute permission="time.contracts.manage">
                <ContractsPage />
              </PermissionRoute>
            }
          />
          {/* D-128: ladders are their own thing — list here, editor at /:id, same split as flows. */}
          <Route
            path="/admin/escalation-paths"
            element={
              <PermissionRoute permission="sla.escalation.manage">
                <EscalationPathsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/escalation-paths/:pathId"
            element={
              <PermissionRoute permission="sla.escalation.manage">
                <EscalationPathEditorPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/company-matching"
            element={
              <PermissionRoute permission="admin.company-matching.manage">
                <CompanyMatchingPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <SettingsPage />
              </AdminRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
